import { apiClient } from '../../../api/client'
import type { PagedResponse, ProxyUserOption, UserProxy, UserProxyCreate } from '../types'

const BASE = '/management/user-proxies'

/**
 * Liste filtreleri (090). Üçü de opsiyoneldir ve sunucuda **VE** mantığıyla birleşir.
 *
 * İki kişi filtresi **kimlik** taşır (serbest metin değil): ad araması kişi seçicisinde yapılır ve
 * seçim tamamlandığında listeye kimlik gider. Böylece seçici ile liste arasında iki ayrı eşleşme
 * mantığı doğmaz.
 */
export interface UserProxyFilters {
  /** Vekalet Veren (`FROM_USER_ID`); `null` → filtresiz. */
  fromUserId?: number | null
  /** Vekalet Verilen (`TO_USER_ID`); `null` → filtresiz. */
  toUserId?: number | null
  /**
   * `RECORD_STATUS`: 1=Aktif · 0=Pasif. **`null` "Tümü" demektir** ve parametre hiç gönderilmez.
   *
   * **`0` geçerli bir değerdir** — yaygın `value || undefined` kalıbı onu da silip "Pasif" filtresini
   * sessizce "Tümü"ye çevirirdi; bu yüzden `?? undefined` ile `null`/`undefined` **açıkça** ayrılır
   * (078'in `minRetryCount` tuzağıyla aynı).
   */
  recordStatus?: number | null
}

export interface UserProxyQuery extends UserProxyFilters {
  page: number
  size: number
}

function toParams(filters: UserProxyFilters) {
  return {
    fromUserId: filters.fromUserId ?? undefined,
    toUserId: filters.toUserId ?? undefined,
    recordStatus: filters.recordStatus ?? undefined,
  }
}

/**
 * Vekalet listesi — **sunucu-taraflı** sayfalama + filtreleme.
 *
 * `sort` **gönderilmez**: düzen sunucuda sabittir (`GRANT_DATE DESC, ID DESC` — en yeni üstte) ve
 * kolon sıralaması bilinçli olarak kapalıdır.
 */
export async function getUserProxies({
  page,
  size,
  ...filters
}: UserProxyQuery): Promise<PagedResponse<UserProxy>> {
  const { data } = await apiClient.get<PagedResponse<UserProxy>>(BASE, {
    params: { page, size, ...toParams(filters) },
  })
  return data
}

/**
 * Kişi seçicisinin seçenekleri: **aktif** kullanıcılarda ad/soyad/sicil araması (en fazla 20 kayıt).
 *
 * Ekranın üç alanı da (iki filtre + formdaki iki alan) bu **tek** uçtan beslenir — hepsi aynı
 * uygunluk kümesini sorar.
 */
export async function searchProxyUsers(q: string): Promise<ProxyUserOption[]> {
  const { data } = await apiClient.get<ProxyUserOption[]>(`${BASE}/users`, { params: { q } })
  return data
}

/**
 * Yeni vekalet. `GRANT_DATE` ve `RECORD_STATUS` **gönderilmez** — sunucu onları yazar (iş saati /
 * daima Aktif).
 *
 * Hatalar: kendine vekalet veya pasif kullanıcı → **400** (`field` ile), aynı çift için zaten aktif
 * bir vekalet varsa → **409 `INVALID_STATE`**.
 */
export async function createUserProxy(body: UserProxyCreate): Promise<UserProxy> {
  const { data } = await apiClient.post<UserProxy>(BASE, body)
  return data
}

/**
 * **Vekaleti kaldır** — sunucuda `RECORD_STATUS = 0 (Pasif)` + `REVOKE_DATE = şimdi`. Kayıt silinMEZ;
 * listede "Pasif" filtresiyle görünmeye devam eder.
 *
 * `modDate` **zorunludur** (optimistic lock): anayasanın idempotent-durum-geçişi istisnası bu uçta
 * uygulanmaz — geçiş idempotent/compare-and-set değil (tekrarı 409 verir, çünkü ilk `REVOKE_DATE`
 * korunur) ve kayıt tek sahipli değil (`USER_PROXY_MANAGE` olan herkes kaldırabilir). Damga listeden
 * gelen satırın `modDate`'idir; ayrı bir okuma gerekmez.
 *
 * Dönen değer **güncellenmiş liste satırıdır** → çağıran onu önbellekteki satırın yerine koyar.
 * Damga bayatsa **409**, zaten Pasif bir kayıtta **409** (arayüz o satırlarda butonu zaten göstermez).
 */
export async function revokeUserProxy(id: number, modDate: string): Promise<UserProxy> {
  const { data } = await apiClient.put<UserProxy>(`${BASE}/${id}/revoke`, { modDate })
  return data
}
